import { apiClient } from '../lib/apiClient'

export type SubscriptionTier = 'starter' | 'professional' | 'enterprise'
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'grace_period' | 'suspended'

export interface Subscription {
  id: string
  org_id: string
  tier: SubscriptionTier
  status: SubscriptionStatus
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  trial_ends_at: string | null
  trial_expired_at: string | null
  grace_period_started_at: string | null
  current_period_start: string
  current_period_end: string
  canceled_at: string | null
  last_payment_failed_at: string | null
  scheduled_tier: string | null
  scheduled_tier_effective_date: string | null
  next_billing_date: string | null
  cancel_at_period_end: boolean
  created_at: string
  updated_at: string
}

export interface Organization {
  id: string
  name: string
  slug: string
  logo_url: string | null
  default_currency: string
  suspended_at: string | null
  grace_period_ends_at: string | null
  permanently_deleted_at: string | null
  created_at: string
}

export interface LifecycleEvent {
  id: string
  org_id: string
  subscription_id: string
  event_type: string
  from_status: string | null
  to_status: string | null
  metadata: Record<string, any>
  created_at: string
}

export type LifecycleState =
  | 'active'
  | 'trialing'
  | 'trial_expired'
  | 'grace_period'
  | 'suspended'
  | 'canceled'
  | 'permanently_deleted'

export interface SubscriptionLimits {
  tier: SubscriptionTier
  max_users: number
  max_projects: number
  max_resources: number
  has_sso: boolean
  has_audit_log: boolean
  has_partner_rates: boolean
  has_revenue_forecast: boolean
  has_gantt: boolean
}

export const PLAN_LABELS: Record<SubscriptionTier, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
}

export const PLAN_PRICES: Record<SubscriptionTier, number> = {
  starter: 79,
  professional: 199,
  enterprise: 499,
}

async function getSubscription(orgId: string): Promise<Subscription | null> {
  try {
    return await apiClient.get<Subscription>(`/orgs/${orgId}/subscription`)
  } catch (err: any) {
    if (err?.status === 404) return null
    throw err
  }
}

async function getLimits(tier: SubscriptionTier): Promise<SubscriptionLimits | null> {
  try {
    return await apiClient.get<SubscriptionLimits>(`/subscription-limits/${tier}`)
  } catch (err: any) {
    if (err?.status === 404) return null
    throw err
  }
}

async function getAllLimits(): Promise<SubscriptionLimits[]> {
  return apiClient.get<SubscriptionLimits[]>('/subscription-limits')
}

function isActive(sub: Subscription | null): boolean {
  if (!sub) return false
  if (sub.status === 'active') return true
  if (sub.status === 'trialing') {
    if (!sub.trial_ends_at) return true
    return new Date(sub.trial_ends_at) > new Date()
  }
  return false
}

function getDaysLeft(sub: Subscription | null): number | null {
  if (!sub || sub.status !== 'trialing' || !sub.trial_ends_at) return null
  const diff = new Date(sub.trial_ends_at).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

function getLifecycleState(sub: Subscription | null, org: Organization | null): LifecycleState {
  if (!sub) return 'canceled'

  if (org?.permanently_deleted_at) {
    return 'permanently_deleted'
  }

  if (sub.status === 'suspended' || org?.suspended_at) {
    return 'suspended'
  }

  if (sub.status === 'grace_period' || org?.grace_period_ends_at) {
    const gracePeriodEnded = org?.grace_period_ends_at && new Date(org.grace_period_ends_at) < new Date()
    if (gracePeriodEnded) {
      return 'suspended'
    }
    return 'grace_period'
  }

  if (sub.status === 'trialing') {
    if (!sub.trial_ends_at) return 'trialing'
    const trialEnded = new Date(sub.trial_ends_at) < new Date()
    return trialEnded ? 'trial_expired' : 'trialing'
  }

  if (sub.status === 'active') {
    return 'active'
  }

  if (sub.status === 'canceled') {
    return 'canceled'
  }

  return 'trial_expired'
}

function getGracePeriodDaysLeft(org: Organization | null): number | null {
  if (!org?.grace_period_ends_at) return null
  const diff = new Date(org.grace_period_ends_at).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

function isReadOnly(lifecycleState: LifecycleState): boolean {
  return lifecycleState === 'grace_period' || lifecycleState === 'suspended'
}

function canReactivate(lifecycleState: LifecycleState, org: Organization | null): boolean {
  if (lifecycleState === 'permanently_deleted') return false

  if (lifecycleState === 'grace_period') {
    if (!org?.grace_period_ends_at) return false
    return new Date(org.grace_period_ends_at) > new Date()
  }

  if (lifecycleState === 'suspended' && org?.suspended_at) {
    const suspendedDate = new Date(org.suspended_at)
    const daysSinceSuspension = (Date.now() - suspendedDate.getTime()) / (1000 * 60 * 60 * 24)
    return daysSinceSuspension < 90
  }

  return false
}

async function enterGracePeriod(orgId: string, subscriptionId: string): Promise<void> {
  await apiClient.post(`/orgs/${orgId}/subscription/enter-grace-period`, { subscriptionId })
}

async function suspendOrganization(orgId: string, subscriptionId: string): Promise<void> {
  await apiClient.post(`/orgs/${orgId}/subscription/suspend`, { subscriptionId })
}

async function reactivateSubscription(orgId: string, subscriptionId: string, stripeSubscriptionId: string): Promise<void> {
  await apiClient.post(`/orgs/${orgId}/subscription/reactivate`, { subscriptionId, stripeSubscriptionId })
}

async function getLifecycleEvents(orgId: string): Promise<LifecycleEvent[]> {
  return apiClient.get<LifecycleEvent[]>(`/orgs/${orgId}/subscription/lifecycle-events`)
}

async function checkAndUpdateLifecycleStatus(orgId: string): Promise<LifecycleState> {
  const result = await apiClient.post<{ lifecycleState: LifecycleState }>(
    `/orgs/${orgId}/subscription/check-lifecycle`,
    {}
  )
  return result.lifecycleState
}

async function getChangePreview(orgId: string, requestedTier: SubscriptionTier) {
  return apiClient.get<any>(
    `/orgs/${orgId}/subscription/change-preview?requestedTier=${requestedTier}`
  )
}

async function upgradePlan(orgId: string, newTier: SubscriptionTier) {
  const successUrl = `${window.location.origin}/billing?checkout=success&orgId=${orgId}&tier=${newTier}`
  const cancelUrl = `${window.location.origin}/billing?checkout=cancel`
  return apiClient.post<any>(`/orgs/${orgId}/subscription/upgrade`, { tier: newTier, successUrl, cancelUrl })
}

async function schedulePlanChange(orgId: string, newTier: SubscriptionTier, effectiveDate?: string) {
  return apiClient.post<any>(`/orgs/${orgId}/subscription/schedule-change`, { tier: newTier, effectiveDate })
}

async function cancelScheduledChange(orgId: string) {
  return apiClient.post<any>(`/orgs/${orgId}/subscription/cancel-scheduled-change`, {})
}

async function createCustomerPortalSession(orgId: string, returnUrl: string) {
  return apiClient.post<any>(`/orgs/${orgId}/subscription/customer-portal`, { returnUrl })
}

export const subscriptionService = {
  getSubscription,
  getLimits,
  getAllLimits,
  isActive,
  getDaysLeft,
  getLifecycleState,
  getGracePeriodDaysLeft,
  isReadOnly,
  canReactivate,
  enterGracePeriod,
  suspendOrganization,
  reactivateSubscription,
  getLifecycleEvents,
  checkAndUpdateLifecycleStatus,
  getChangePreview,
  upgradePlan,
  schedulePlanChange,
  cancelScheduledChange,
  createCustomerPortalSession,
}
